import { useEffect, useState } from 'react'
import { ClipboardText, Hourglass, Package, Truck } from '@/components/icons'
import { MetricCard } from '@/components/ui/metric-card'
import { compactAge, compactInr, num } from '@/lib/format'
import type { OrderSummary } from '@/lib/vision/schemas'
import { dateBounds } from './date-range'
import { useOrdersUrlState } from './url-state'
import { useOrdersSummaryQuery } from './queries'

/**
 * Four metric cards at the top of the Purchase orders queue: POs / Ordered /
 * Sent to warehouse / Pending for the **current filter set**,
 * not the current page. Vision `GET /v1/orders/summary` owns the aggregate.
 *
 * `from`/`to` come from `dateBounds`, not from the URL: the relative pills
 * write only `range=7d` and leave `from`/`to` off, so reading the params
 * directly would leave the cards unfiltered on every preset but Custom.
 * `bcSalesOrder` rides along for the same reason the other filters do: the
 * cards claim to describe the current filter set.
 */
export function PoListSummary() {
  const [state] = useOrdersUrlState()
  const dateWindow = dateBounds(state.date)
  const query = useOrdersSummaryQuery({
    channel: state.channels.length ? state.channels : undefined,
    channelCode: state.channelCode,
    vendorCode: state.vendorCodes.length ? state.vendorCodes : undefined,
    status: state.statuses.length ? state.statuses : undefined,
    // Keep the cards on the same universe as the rows beneath them — a BC
    // filter that moved the queue but not the totals would just look broken.
    bcSalesOrder: state.bcSalesOrder.length ? state.bcSalesOrder : undefined,
    q: state.q || undefined,
    from: dateWindow.from,
    to: dateWindow.to,
  })

  return (
    <PoListSummaryStrip
      summary={query.data}
      loading={query.isLoading && !query.data}
      refreshedAt={query.dataUpdatedAt}
    />
  )
}

export function PoListSummaryStrip({
  summary,
  loading = false,
  refreshedAt,
}: {
  summary?: OrderSummary | null
  loading?: boolean
  refreshedAt?: number
}) {
  const freshness = useFreshness(refreshedAt)

  return (
    <div
      data-testid="po-list-summary"
      aria-busy={loading || undefined}
      aria-live="polite"
      className="grid grid-cols-2 gap-3 md:flex md:flex-wrap md:items-stretch"
    >
      <MetricCard
        className="md:min-w-60"
        icon={ClipboardText}
        label="Purchase orders"
        value={summary ? num(summary.poCount) : '—'}
        amount={freshness}
        loading={loading}
      />
      <MetricCard
        className="md:min-w-60"
        icon={Package}
        label="Ordered quantity"
        value={summary ? num(summary.orderedQty) : '—'}
        amount={summary ? compactInr(summary.orderedValue) : '—'}
        loading={loading}
      />
      <MetricCard
        className="md:min-w-60"
        icon={Truck}
        label="Sent to warehouse"
        value={summary ? num(summary.sentToWarehouseQty) : '—'}
        amount={summary ? compactInr(summary.sentToWarehouseValue) : '—'}
        loading={loading}
      />
      <MetricCard
        className="md:min-w-60"
        icon={Hourglass}
        label="Pending quantity"
        value={summary ? num(summary.remainingQty) : '—'}
        amount={summary ? compactInr(summary.remainingValue) : '—'}
        loading={loading}
      />
    </div>
  )
}

export function useFreshness(refreshedAt?: number): string {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    setNow(Date.now())
    const timer = window.setInterval(() => setNow(Date.now()), 30_000)
    return () => window.clearInterval(timer)
  }, [refreshedAt])

  if (!refreshedAt) return 'Not refreshed'
  const age = compactAge(refreshedAt, new Date(now))
  return age === 'just now' ? 'Refreshed now' : `Refreshed ${age ?? 'now'}`
}
